// i18n bootstrap. Three locales for V1: English (default), Simplified
// Chinese, and Latin American Spanish. URL strategy is path-prefix —
// /zh/foo and /es/foo for those, plain /foo for English (default). The
// active language is driven by the URL, not by browser detection — see
// LanguageSync in App.tsx for the URL → i18next sync.
//
// Translation files live under src/i18n/locales/{lang}/{namespace}.json.
// ONLY English is bundled into the main chunk: it is the fallback language
// and ~2/3 of visitors, so bundling it keeps the common path free of an
// extra round trip. The other seven languages are separate lazy chunks
// pulled in by loadLanguage() — previously all eight were statically
// imported, which shipped ~550 KB of JSON nobody reads to every visitor and
// was the largest single slice of the render-blocking bundle.
//
// New namespaces need adding to the English imports + resources block; the
// other languages are picked up automatically by the glob.

import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

import enCommon from './locales/en/common.json'
import enAbout from './locales/en/about.json'
import enFaq from './locales/en/faq.json'
import enLanding from './locales/en/landing.json'
import enDestinations from './locales/en/destinations.json'
import enDetail from './locales/en/detail.json'
import enHow from './locales/en/how.json'
import enCompat from './locales/en/compat.json'
import enAuth from './locales/en/auth.json'
import enCheckout from './locales/en/checkout.json'
import enOrder from './locales/en/order.json'
import enAccount from './locales/en/account.json'
import enPromise from './locales/en/promise.json'
import enPrivacy from './locales/en/privacy.json'
// China landing page — its own namespace because the copy is specific to that
// one page and doesn't overlap the shared destination-detail strings.
import enCnlp from './locales/en/cnlp.json'
// Affiliate partner portal — a separate audience (travel agencies) with its
// own vocabulary, so it doesn't share the customer-facing namespaces.
import enPartner from './locales/en/partner.json'

export const SUPPORTED_LANGUAGES = ['en', 'zh', 'es', 'fr', 'ja', 'ko', 'de', 'it'] as const
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]

// Path-prefix set used to detect / strip locale prefixes (everything but
// English uses a /{lang} URL prefix). Declared here, before the i18n
// init block, so languageFromPath can use it during the module-init
// computation of `initialLanguage` — moving it later triggers a TDZ
// crash on first load.
const LOCALE_PREFIXES: ReadonlySet<string> = new Set(
  SUPPORTED_LANGUAGES.filter((l) => l !== 'en'),
)

export const DEFAULT_LANGUAGE: SupportedLanguage = 'en'

export const LANGUAGE_NAMES: Record<SupportedLanguage, string> = {
  en: 'English',
  zh: '中文',
  es: 'Español',
  fr: 'Français',
  ja: '日本語',
  ko: '한국어',
  de: 'Deutsch',
  it: 'Italiano',
}

// Compact label used in the nav pill.
export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  en: 'EN',
  zh: '中文',
  es: 'ES',
  fr: 'FR',
  ja: '日本語',
  ko: '한국어',
  de: 'DE',
  it: 'IT',
}

// Lazily-loaded translations for every language except English.
//
// English is EXCLUDED deliberately. It is statically imported above, and a
// module that is both statically and dynamically imported gets split into its
// own chunk — which Vite then modulepreloads as an entry dependency. Globbing
// `*` put all fourteen English namespaces on the critical path as separate
// 1-2 KB requests, so every visitor paid fourteen round trips to fetch copy
// that was already in the entry bundle. On an origin with no CDN a round trip
// costs far more than the ~17 KB gz of inlining them.
const LAZY_LOCALES = import.meta.glob([
  './locales/*/*.json',
  '!./locales/en/**',
]) as Record<string, () => Promise<{ default: Record<string, unknown> }>>

/** Track which languages are fully present, and which are mid-flight.
 *
 *  Asking i18next (`hasResourceBundle`) is not good enough: the namespaces
 *  load in parallel, so `common` — one of the smallest — routinely lands
 *  first and would report the language as ready while twelve others are
 *  still in the air. It also cannot tell "loaded" from "loaded except the
 *  two that 404'd", which would strand a customer on English copy for the
 *  rest of the session with no retry. */
const loadedLanguages = new Set<string>()
const inFlight = new Map<string, Promise<void>>()

/** Ensure `lng`'s translations are in i18next before anything renders in it.
 *
 *  Resolves immediately for English (bundled) and for any language already
 *  fully loaded, so callers can await it unconditionally. Never rejects: a
 *  missing chunk should degrade to English copy, never a blank page — but a
 *  language that only partly loaded is deliberately NOT marked done, so the
 *  next call retries it. */
export function loadLanguage(lng: SupportedLanguage): Promise<void> {
  if (lng === DEFAULT_LANGUAGE || loadedLanguages.has(lng)) return Promise.resolve()
  const pending = inFlight.get(lng)
  if (pending) return pending

  const prefix = `./locales/${lng}/`
  const run = (async () => {
    const results = await Promise.allSettled(
      Object.entries(LAZY_LOCALES)
        .filter(([path]) => path.startsWith(prefix))
        .map(async ([path, load]) => {
          const mod = await load()
          const ns = path.slice(prefix.length).replace(/\.json$/, '')
          i18n.addResourceBundle(lng, ns, mod.default, true, true)
        }),
    )
    inFlight.delete(lng)
    if (results.length > 0 && results.every((r) => r.status === 'fulfilled')) {
      loadedLanguages.add(lng)
    }
  })()
  inFlight.set(lng, run)
  return run
}

// Pick the initial language from the URL at module load — before React
// mounts. This means the very first render uses the correct language,
// avoiding an EN flash on /zh or /es routes. LanguageSync in App.tsx
// still handles in-app navigation changes after mount.
const initialLanguage: SupportedLanguage =
  typeof window !== 'undefined' ? languageFromPath(window.location.pathname) : DEFAULT_LANGUAGE

void i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: { common: enCommon, about: enAbout, faq: enFaq, landing: enLanding, destinations: enDestinations, detail: enDetail, how: enHow, compat: enCompat, auth: enAuth, checkout: enCheckout, order: enOrder, account: enAccount, promise: enPromise, privacy: enPrivacy, cnlp: enCnlp, partner: enPartner },
    },
    // The seven lazy languages arrive later via addResourceBundle; without
    // this i18next treats a language with no bundle as wholly missing rather
    // than falling back namespace by namespace.
    partialBundledLanguages: true,
    ns: ['common', 'about', 'faq', 'landing', 'destinations', 'detail', 'how', 'compat', 'auth', 'checkout', 'order', 'account', 'promise', 'privacy', 'cnlp', 'partner'],
    lng: initialLanguage,
    fallbackLng: DEFAULT_LANGUAGE,
    defaultNS: 'common',
    interpolation: { escapeValue: false }, // React already escapes
    returnNull: false,
  })

if (typeof document !== 'undefined') {
  document.documentElement.lang = htmlLangFor(initialLanguage)
}

export default i18n

// HTML lang attribute value for each supported locale. Spanish gets
// 'es-419' (Latin American Spanish per BCP-47), matching the locale we
// target in our translation copy. French = generic 'fr' (France-flavored
// but understandable across the Francophone world). Japanese = 'ja'.
export function htmlLangFor(lang: SupportedLanguage): string {
  if (lang === 'zh') return 'zh-CN'
  if (lang === 'es') return 'es-419'
  if (lang === 'fr') return 'fr'
  if (lang === 'ja') return 'ja'
  if (lang === 'ko') return 'ko'
  if (lang === 'de') return 'de'
  if (lang === 'it') return 'it'
  return 'en'
}

// BCP-47 tag to pass into Intl APIs (toLocaleDateString, etc.).
// undefined defers to the browser's default for English so dates look
// native ("May 25, 2026" in en-US, "25 May 2026" in en-GB) instead of
// being forced to a single English variant.
export function dateLocaleFor(lang: SupportedLanguage): string | undefined {
  if (lang === 'zh') return 'zh-CN'
  if (lang === 'es') return 'es-419'
  if (lang === 'fr') return 'fr-FR'
  if (lang === 'ja') return 'ja-JP'
  if (lang === 'ko') return 'ko-KR'
  if (lang === 'de') return 'de-DE'
  if (lang === 'it') return 'it-IT'
  return undefined
}

// Parse the current language out of a pathname. Looks at the first
// path segment — 'zh' or 'es' → the matching locale, anything else
// (including missing) defaults to English. Used by LanguageSync to
// keep i18next in sync with the URL.
export function languageFromPath(pathname: string): SupportedLanguage {
  const first = pathname.split('/').filter(Boolean)[0]
  if (first && LOCALE_PREFIXES.has(first)) return first as SupportedLanguage
  return 'en'
}

// Build the equivalent path in another language. Examples:
//   ('/destinations/jp', 'zh') → '/zh/destinations/jp'
//   ('/zh/destinations/jp', 'en') → '/destinations/jp'
//   ('/zh', 'fr') → '/fr'
//   ('/es', 'en') → '/'
export function pathInLanguage(pathname: string, lang: SupportedLanguage): string {
  const segs = pathname.split('/').filter(Boolean)
  const hasLocalePrefix = segs.length > 0 && LOCALE_PREFIXES.has(segs[0])
  const tail = hasLocalePrefix ? segs.slice(1) : segs
  const newSegs = lang === 'en' ? tail : [lang, ...tail]
  return '/' + newSegs.join('/')
}

// Convenience for the common case: build a locale-prefixed path from
// an unprefixed base. `withLang('/destinations', 'zh')` → '/zh/destinations'.
// Used everywhere Link/navigate need to stay in language; delegates to
// pathInLanguage so it works whether the caller has a prefixed or bare path.
export function withLang(path: string, lang: SupportedLanguage): string {
  return pathInLanguage(path, lang)
}
